import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  Alert,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ArrowForwardIosSharp, DeleteOutline } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { useFormContext, FieldValues } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';
import { formatDate } from 'src/shared/functions/formatDate';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { AccordionGroup } from 'src/shared/components/organisms/accordionGroup';
import { createFeePeriod, deleteFeePeriod, getFeePeriods } from 'src/shared/services/feePeriodService';
import { FeeLevelEnum, FeePeriod } from '../models/feePeriod';
import { FeePeriodSummary } from './feePeriodSummary';
import { FeePeriodEditor } from './feePeriodEditor';

type FeePeriodSectionProps = {
  level: FeeLevelEnum;
  associationId: number;
  autogiroFee?: boolean;
  amountHeader: string;
  showFriendFee?: boolean;
  distributedFee?: boolean;
  /** Profilerna styrs av förbundet och kan inte tas bort här. */
  lockedProfiles?: boolean;
  /** Sant när avgiften inte får ändras här, till exempel när förbundet sköter den. */
  readOnlyMode?: boolean;
  /**
   * Inställningar som gäller oavsett period, till exempel aviseringsmetoden. De ligger ovanför
   * listan och inte inuti en period, eftersom de inte kan skilja sig mellan perioderna.
   *
   * Får veta om det finns en kommande avgift, eftersom aviseringsmetoden är låst så länge en
   * sådan finns. Metoden följer nämligen med perioden och skulle skrivas tillbaka när den
   * aktiveras.
   */
  header?: (state: { hasPlannedPeriods: boolean }) => ReactNode;
  /**
   * Avgiftsbeloppen, det som faktiskt hör till den period som gäller nu.
   *
   * Får veta om det finns en kommande avgift. Den bygger på beloppen som gäller nu, så de
   * låses medan den ligger och väntar.
   */
  children: (state: { hasPlannedPeriods: boolean }) => ReactNode;
};

/**
 * Avgifterna över tid. Formuläret för den avgift som gäller nu ligger överst under "Gäller
 * löpande", och under det ligger avgifter som ska börja gälla och avgifter som har gällt.
 *
 * "Skapa nya avgifter" frågar bara efter ett startdatum och skapar perioden som en kopia av
 * den avgift som gäller nu. Beloppen fylls sedan i direkt i perioden, som har ett eget
 * formulär. Sidans formulär hör till nuet och ska inte behöva röras för att planera en
 * framtida höjning.
 */
export const FeePeriodSection = (props: FeePeriodSectionProps) => {
  const { level, associationId, autogiroFee, amountHeader, showFriendFee, distributedFee, lockedProfiles, readOnlyMode, header, children } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { getValues } = useFormContext<FieldValues>();

  const [periods, setPeriods] = useState<FeePeriod[]>([]);
  const [currentValidFrom, setCurrentValidFrom] = useState<string>('');
  const [expandedId, setExpandedId] = useState<number | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [validFrom, setValidFrom] = useState<Dayjs | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  /**
   * Slutdatum för den avgift som just stängdes, när en ny period började gälla direkt. Då finns
   * ingen kommande period att fylla i, de nya beloppen skrivs i formuläret för den löpande
   * avgiften, och det behöver sägas rakt ut.
   */
  const [closedUntil, setClosedUntil] = useState<string | undefined>(undefined);

  const fetchPeriods = useCallback(async () => {
    // Förbundet finns i en enda uppsättning per kund och pekas ut av API:et, därför är 0
    // ett giltigt id där. Distrikt och förening måste ha ett riktigt id.
    if (level !== FeeLevelEnum.CountryAss && !associationId) return [];

    try {
      const result = await getFeePeriods(level, associationId);

      if (isMounted()) {
        setPeriods(result.data?.periods ?? []);
        setCurrentValidFrom(result.data?.currentValidFrom ?? '');
      }

      return result.data?.periods ?? [];
    } catch (e) {
      // Perioderna är extra information, avgiftsformuläret fungerar utan dem.
      ConsoleHelper.log(e);
      return [];
    }
  }, [level, associationId, isMounted]);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  const planned = periods.filter((x) => x.isPlanned);
  // Historiken läses bakifrån, den period som gällde senast är den som oftast efterfrågas.
  const previous = periods.filter((x) => !x.isPlanned).sort((a, b) => b.validFrom.localeCompare(a.validFrom));

  /** Nästa årsskifte är det överlägset vanligaste bytesdatumet, så det får bli förslaget. */
  const defaultValidFrom = () => dayjs().add(1, 'year').startOf('year');

  const openDialog = () => {
    setValidFrom(defaultValidFrom());
    setDialogOpen(true);
  };

  const onCreate = async () => {
    if (!validFrom) return;

    const date8 = validFrom.format('YYYYMMDD');

    setSaving(true);

    try {
      await createFeePeriod(level, associationId, { ...getValues(), validFrom: date8 });

      const updated = await fetchPeriods();

      if (isMounted()) {
        setDialogOpen(false);

        const created = updated.find((x) => x.validFrom === date8);

        if (created) {
          // Öppna den nya perioden direkt, det är där beloppen ska fyllas i.
          setExpandedId(created.id);
          setClosedUntil(undefined);
          toast.success(t('association:feePeriodCreated') as string);
        } else {
          // Startdatumet är inne, så perioden flyttades in med en gång och finns inte kvar som
          // kommande. De nya beloppen skrivs då i formuläret för den löpande avgiften.
          setClosedUntil(validFrom.subtract(1, 'day').format('YYYY-MM-DD'));
          toast.success(t('association:feePeriodActivated') as string);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (isMounted()) toast.error((e?.data?.messages ?? e?.data ?? t('association:feePeriodCreateFailed')) as string);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  const onDelete = async (period: FeePeriod) => {
    try {
      await deleteFeePeriod(level, period.id);

      if (isMounted()) {
        toast.success(t('association:feePeriodDeleted') as string);
        fetchPeriods();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (isMounted()) toast.error((e?.data?.messages ?? e?.data ?? t('association:feePeriodDeleteFailed')) as string);
    }
  };

  const periodHeading = (period: FeePeriod) =>
    period.validTo
      ? t('association:feePeriodRange', { from: formatDate(period.validFrom), to: formatDate(period.validTo) })
      : t('association:feePeriodFrom', { from: formatDate(period.validFrom) });

  const plannedAccordion = (period: FeePeriod) => (
    <Accordion
      disableGutters
      elevation={0}
      key={period.id}
      expanded={expandedId === period.id}
      onChange={(_, isExpanded) => setExpandedId(isExpanded ? period.id : undefined)}
    >
      <AccordionSummary expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
          <Typography sx={{ fontWeight: 500 }}>{periodHeading(period)}</Typography>

          {!readOnlyMode && (
            <Tooltip title={t('association:feePeriodDelete')} placement='left'>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(period);
                }}
              >
                <DeleteOutline fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <FeePeriodEditor
          level={level}
          period={period}
          autogiroFee={autogiroFee}
          amountHeader={amountHeader}
          showFriendFee={showFriendFee}
          distributedFee={distributedFee}
          lockedProfiles={lockedProfiles}
          onSaved={fetchPeriods}
        />
      </AccordionDetails>
    </Accordion>
  );

  const previousAccordion = (period: FeePeriod) => (
    <Accordion disableGutters elevation={0} key={period.id}>
      <AccordionSummary expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}>
        <Typography sx={{ fontWeight: 500 }}>{periodHeading(period)}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FeePeriodSummary fee={period.fee} autogiroFee={autogiroFee} amountHeader={amountHeader} />

        {period.changedBy && (
          <Typography variant='caption' sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
            {t('association:feePeriodChangedBy', { name: period.changedBy })}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );

  const startsToday = validFrom !== null && !validFrom.isAfter(dayjs(), 'day');

  return (
    <>
      {closedUntil && (
        <Alert severity='info' sx={{ mb: 2 }} onClose={() => setClosedUntil(undefined)}>
          {t('association:feePeriodActivatedInfo', { to: closedUntil })}
        </Alert>
      )}

      {header?.({ hasPlannedPeriods: planned.length > 0 })}

      {/* En sammanhängande lista: nuet överst, sedan det som ska börja gälla och sist det som
          har gällt. Varje rubrik säger vilken period den avser, så inga mellanrubriker behövs. */}
      <AccordionGroup cardContent>
        <Accordion disableGutters elevation={0} defaultExpanded>
          <AccordionSummary expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}>
            <Typography sx={{ fontWeight: 500 }}>
              {t('association:feePeriodCurrent')}
              {currentValidFrom && currentValidFrom !== '19000101' && ` (${t('association:feePeriodSince', { from: formatDate(currentValidFrom) })})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {planned.length > 0 && (
              <Alert severity='info' sx={{ mb: 2 }}>
                {t('association:feeLockedByPlanned')}
              </Alert>
            )}

            {children({ hasPlannedPeriods: planned.length > 0 })}
          </AccordionDetails>
        </Accordion>

        {planned.map((period) => plannedAccordion(period))}

        {previous.map((period) => previousAccordion(period))}
      </AccordionGroup>

      {!readOnlyMode && (
        <Box sx={{ mt: 2 }}>
          <Button variant='outlined' size='small' disableElevation onClick={openDialog}>
            <Add fontSize='small' />
            {t('association:feePeriodCreate')}
          </Button>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>{t('association:feePeriodCreate')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>{t('association:feePeriodCreateInfo')}</DialogContentText>

          <DatePicker
            label={t('association:feePeriodValidFrom')}
            format='YYYY-MM-DD'
            value={validFrom}
            minDate={dayjs()}
            displayWeekNumber
            onChange={(value) => setValidFrom(value)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                variant: 'outlined',
                InputLabelProps: { shrink: true },
              },
            }}
          />

          {startsToday && (
            <Alert severity='info' sx={{ mt: 2 }}>
              {t('association:feePeriodCreateTodayInfo')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common:button.cancel')}</Button>
          <Button variant='contained' disableElevation disabled={!validFrom || saving} onClick={onCreate}>
            {t('common:button.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
